import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import { PRESET_NAMES, mediumName, presetParts } from '../../utils/cultureMedia';

/*
 * Sterile field isolation, for the staff who may perform it
 * (routes/field_isolation.py; Part III section 20, PR11): check in with the
 * current fix -- the server insists the whole accuracy circle lies inside a
 * live fire-safe zone -- then confirm every item of the safety checklist and
 * record the dish. The organisers' kill switch reaches this panel with the
 * next heartbeat and stops everything at once.
 */
const CHECKLIST = ['no_fire_ban', 'zone_clear', 'water_ready', 'candle_stable', 'ethanol_capped', 'scalpel_flamed',
  'dish_sealed', 'flame_out'];

export default function IsolationPanel({ missionId, fix, isolation, finds = [] }) {
  const { t } = useTranslation('events');
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState({ checklist: [], medium: 'PDA', antibiotics: ['oxytetracycline', 'chloramphenicol'],
    dish_label: '', sample_id: '', notes: '' });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const stopped = !!isolation?.killed;

  const load = useCallback(() => api.get(`/events/${missionId}/isolations`)
    .then((r) => setOpen((r.data.items || []).find((i) => i.status === 'checked_in') || null))
    .catch(() => {}), [missionId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (stopped) load(); }, [stopped, load]);

  if (!isolation?.enabled) return null;

  const checkin = async () => {
    setError('');
    setMessage('');
    if (!fix) { setError(t('isolation.noFix')); return; }
    try {
      const r = await api.post(`/events/${missionId}/isolations/checkin`, { pos: {
        lat: fix.lat, lng: fix.lng, acc_m: fix.accuracy_m, fix_at: new Date(fix.at || Date.now()).toISOString() } });
      setOpen({ isolation_id: r.data.isolation_id, zone_name: r.data.zone_name, status: 'checked_in' });
    } catch (e) {
      setError(errorText(e));
    }
  };
  const toggle = (key, value) => setForm((f) => ({ ...f, [key]: f[key].includes(value)
    ? f[key].filter((x) => x !== value) : [...f[key], value] }));
  const record = async () => {
    setError('');
    try {
      await api.post(`/events/${missionId}/isolations/${open.isolation_id}/record`, {
        ...form, sample_id: form.sample_id || null, notes: form.notes || null });
      setOpen(null);
      setMessage(t('isolation.recorded'));
    } catch (e) {
      setError(errorText(e));
    }
  };
  const abandon = async () => {
    try {
      await api.post(`/events/${missionId}/isolations/${open.isolation_id}/abandon`);
      setOpen(null);
    } catch (e) {
      setError(errorText(e));
    }
  };

  return (
    <section data-testid="isolation-panel" data-state={stopped ? 'stopped' : open ? 'open' : 'idle'}
      aria-labelledby="isolation-title" style={ps.box}>
      <h3 id="isolation-title" style={{ margin: '0 0 6px', fontSize: 15 }}>{t('isolation.title')}</h3>
      {stopped && <p role="alert" style={ps.stop} data-testid="isolation-stopped">{t('isolation.stopped')}</p>}
      {!stopped && !open && (
        <>
          <p style={{ fontSize: 13, margin: '0 0 8px' }}>{t('isolation.checkinHelp')}</p>
          <button type="button" className="pn-btn" style={{ minHeight: 44 }} onClick={checkin}
            data-testid="isolation-checkin">{t('isolation.checkin')}</button>
        </>
      )}
      {!stopped && open && (
        <div style={{ display: 'grid', gap: 8 }} data-testid="isolation-form">
          <p style={{ margin: 0 }}>{t('isolation.inZone', { zone: open.zone_name || '' })}</p>
          <fieldset style={ps.fieldset}>
            <legend>{t('isolation.checklist')}</legend>
            {CHECKLIST.map((item) => (
              <label key={item} style={ps.check}>
                <input type="checkbox" checked={form.checklist.includes(item)} onChange={() => toggle('checklist', item)}
                  data-testid={`isolation-check-${item}`} />
                <span>{t(`isolation.check.${item}`)}</span>
              </label>
            ))}
          </fieldset>
          {/* One of the four standard media (utils/cultureMedia.js): a base
              medium with or without both antibiotics, sent as the server
              records it (medium + antibiotics). */}
          <label style={ps.field}><span>{t('isolation.medium')}</span>
            <select value={mediumName(form.medium, form.antibiotics)} data-testid="isolation-medium"
              onChange={(e) => setForm({ ...form, ...presetParts(e.target.value) })}>
              {PRESET_NAMES.map((name) => <option key={name} value={name}>{name}</option>)}
            </select></label>
          <label style={ps.field}><span>{t('isolation.label')}</span>
            <input value={form.dish_label} maxLength={60} onChange={(e) => setForm({ ...form, dish_label: e.target.value })}
              data-testid="isolation-label" /></label>
          {finds.length > 0 && (
            <label style={ps.field}><span>{t('isolation.find')}</span>
              <select value={form.sample_id} onChange={(e) => setForm({ ...form, sample_id: e.target.value })}
                data-testid="isolation-find">
                <option value="">{t('isolation.noFind')}</option>
                {finds.map((c) => <option key={c.sample_id} value={c.sample_id}>{c.taxon_name || t('card.unnamed')}</option>)}
              </select></label>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="pn-btn" style={{ minHeight: 44 }} data-testid="isolation-record"
              disabled={form.checklist.length !== CHECKLIST.length || !form.dish_label.trim()}
              onClick={record}>{t('isolation.record')}</button>
            <button type="button" className="pn-btn-ghost" style={{ minHeight: 44 }} onClick={abandon}
              data-testid="isolation-abandon">{t('isolation.abandon')}</button>
          </div>
        </div>
      )}
      {message && <p role="status" data-testid="isolation-message">{message}</p>}
      {error && <p role="alert" style={{ color: 'var(--c-danger, #c62828)' }}>{error}</p>}
    </section>
  );
}

const ps = {
  box: { border: '1px solid var(--c-border)', borderRadius: 12, padding: 10, marginTop: 10 },
  stop: { background: 'var(--c-danger-soft, #ffebee)', color: 'var(--c-danger, #c62828)', padding: 8, borderRadius: 8 },
  fieldset: { border: '1px solid var(--c-border)', borderRadius: 8, padding: 8, margin: 0 },
  check: { display: 'flex', gap: 8, alignItems: 'center', minHeight: 36 },
  field: { display: 'grid', gap: 4 },
};
